import { browserPresetWorkspace } from "../presets/browser-workspace.js";
import {
  decodeFishydexPresetPayload,
  defaultFishydexPresetPayload,
} from "../presets/fishydex-payload.js";
import {
  applyTitleOwnership,
  catchTitleIntents,
  projectTitleProgress,
  pruneCatchTitleOwnership,
  requireCatchCount,
  titleProgressIntent,
  writeCatchCount,
} from "./title-progress.js";
import {
  browserFishydexGuideEvidenceRepository,
} from "./guide-evidence-repository.js";
import {
  fishydexBrowserStorage,
  removeAndVerify,
} from "./browser-storage-provider.js";
import {
  FISHYDEX_LEGACY_SEMANTIC_TRANSITION_KEY,
  fishydexLegacySemanticTransition,
  removeFishydexLegacySemanticTransition,
  writeFishydexLegacySemanticTransition,
} from "./legacy-semantic-transition.js";

const COLLECTION_KEY = "fishydex-presets";
const SLOT = "__fishystuffFishydexCurrentV1";
const LEGACY_KEYS = Object.freeze({
  caught: "fishystuff.fishydex.caught.v1",
  favourites: "fishystuff.fishydex.favourites.v1",
  guide: "fishystuff.fishydex.fish-guide.v1",
  ui: "fishystuff.fishydex.ui.v1",
});
const MAX_OPERATION_ID_CHARS = 160;
const MAX_CURRENT_REVISION_CHARS = 1_024;

export const FISHYDEX_CURRENT_BRAND = Symbol.for("fishystuff.fishydex-current.v1");

function clone(value) {
  return value === undefined ? undefined : structuredClone(value);
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }
  for (const child of Object.values(value)) {
    deepFreeze(child);
  }
  return Object.freeze(value);
}

function frozenClone(value) {
  return deepFreeze(clone(value));
}

function requireWorkspace(workspace) {
  for (const method of [
    "bindCollectionRuntime",
    "clearCurrent",
    "edit",
    "exportRawCollection",
    "initialize",
    "load",
    "state",
    "subscribe",
  ]) {
    if (typeof workspace?.[method] !== "function") {
      throw new TypeError(`Fishydex Current workspace is missing ${method}()`);
    }
  }
  return workspace;
}

function requireStorage(storage) {
  for (const method of ["getItem", "setItem", "removeItem"]) {
    if (typeof storage?.[method] !== "function") {
      throw new TypeError(`Fishydex legacy storage is missing ${method}()`);
    }
  }
  return storage;
}

function requireEvidence(repository) {
  for (const method of ["activate", "ensure", "load", "resolve", "state", "subscribe"]) {
    if (typeof repository?.[method] !== "function") {
      throw new TypeError(`Fishydex Guide evidence repository is missing ${method}()`);
    }
  }
  return repository;
}

function canonicalIds(value, label) {
  if (!Array.isArray(value)) {
    throw new TypeError(`${label} must be an array`);
  }
  const ids = value.map((candidate, index) => {
    if (!Number.isInteger(candidate) || candidate < 1 || candidate > 4_294_967_295) {
      throw new TypeError(`${label}[${index}] must be a positive u32`);
    }
    return candidate;
  }).sort((left, right) => left - right);
  return ids.filter((candidate, index) => index === 0 || candidate !== ids[index - 1]);
}

function positiveItemId(value) {
  if (!Number.isInteger(value) || value < 1 || value > 4_294_967_295) {
    throw new TypeError("Fishydex item ID must be a positive u32");
  }
  return value;
}

function boolean(value, label) {
  if (typeof value !== "boolean") {
    throw new TypeError(`${label} must be a boolean`);
  }
  return value;
}

function exactOptions(value, fields, label) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const allowed = new Set(fields);
  if (Object.keys(value).some((field) => !allowed.has(field))) {
    throw new TypeError(`${label} has unsupported fields`);
  }
  return value;
}

function opaqueRevision(value, label) {
  if (
    typeof value !== "string"
    || !value
    || value !== value.trim()
    || value.length > MAX_CURRENT_REVISION_CHARS
    || /[\u0000-\u001f\u007f]/.test(value)
  ) {
    throw new TypeError(`${label} must be bounded canonical text`);
  }
  return value;
}

function nonNegativeRevision(value, label) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${label} must be a non-negative safe integer`);
  }
  return value;
}

function defaultPayload() {
  return defaultFishydexPresetPayload();
}

function operationId(value, prefix = "fishydex-current") {
  if (value !== undefined) {
    if (
      typeof value !== "string"
      || !value.trim()
      || value !== value.trim()
      || value.length > MAX_OPERATION_ID_CHARS
      || /[\u0000-\u001f\u007f]/.test(value)
    ) {
      throw new TypeError("Fishydex operation ID must be bounded text");
    }
    return value;
  }
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) {
    return `${prefix}.${uuid}`;
  }
  return `${prefix}.${Date.now().toString(36)}.${Math.random().toString(36).slice(2)}`;
}

function currentEditOptions(value, actionKind) {
  const parsed = exactOptions(
    value,
    ["expectedRevision", "operationId"],
    `Fishydex ${actionKind} command options`,
  );
  return {
    actionKind,
    operationId: operationId(parsed.operationId),
    // Manual intent commands own only the Current workspace. An unrelated
    // Fish Guide activation therefore does not invalidate this revision.
    expectedRevision: parsed.expectedRevision === undefined
      ? undefined
      : opaqueRevision(parsed.expectedRevision, "Fishydex workspace expected revision"),
  };
}

function titleEditOptions(value, actionKind) {
  const parsed = exactOptions(value, ["expectedRevision", "operationId", "encyclopediaKey", "titles"], actionKind);
  if (parsed.encyclopediaKey !== undefined
      && (!Number.isInteger(parsed.encyclopediaKey) || parsed.encyclopediaKey < 1
        || parsed.encyclopediaKey > 65_535)) {
    throw new TypeError("Fishydex title encyclopediaKey must be a positive u16");
  }
  return {
    ...currentEditOptions({
      expectedRevision: parsed.expectedRevision,
      operationId: parsed.operationId,
    }, actionKind),
    encyclopediaKey: parsed.encyclopediaKey,
    titles: parsed.titles,
  };
}

function guideActivationOptions(value) {
  const parsed = exactOptions(
    value,
    ["expectedGuideRevision", "expectedRevision", "operationId"],
    "Fish Guide snapshot activation options",
  );
  return {
    operationId: operationId(parsed.operationId, "fishydex-guide-activation"),
    expectedRevision: parsed.expectedRevision === undefined
      ? undefined
      : opaqueRevision(parsed.expectedRevision, "Fishydex Current expected revision"),
    expectedGuideRevision: parsed.expectedGuideRevision === undefined
      ? undefined
      : nonNegativeRevision(
        parsed.expectedGuideRevision,
        "Fish Guide active expected revision",
      ),
  };
}

function commandResult(projection, operation, fields = {}) {
  return frozenClone({ ...projection, ...fields, operationId: operation });
}

function normalizeWorkspaceProjection(value) {
  const authoritative = value?.authoritative ?? null;
  const source = value?.kind === "ready" ? value : authoritative?.kind === "ready" ? authoritative : null;
  if (!source) {
    return frozenClone({
      kind: value?.kind ?? "uninitialized",
      revision: value?.revision ?? null,
      workspaceRevision: value?.revision ?? null,
      caughtIds: null,
      favouriteIds: null,
      showDried: null,
      catchCounts: null,
      obtainedTitleIds: null,
      showContestTitles: null,
      showUnobtainableTitles: null,
      fishGuideProgress: null,
      payload: null,
      conflict: value?.kind === "conflict" ? clone(value) : null,
      reason: value?.reason ?? null,
    });
  }
  const payload = decodeFishydexPresetPayload(source.current.payload);
  return frozenClone({
    kind: value.kind,
    revision: source.revision,
    workspaceRevision: source.revision,
    caughtIds: payload.catchCounts.filter((entry) => entry.count > 0).map((entry) => entry.itemId),
    favouriteIds: payload.favouriteIds.slice(),
    showDried: payload.showDried,
    catchCounts: clone(payload.catchCounts),
    obtainedTitleIds: payload.obtainedTitleIds.slice(),
    showContestTitles: payload.showContestTitles,
    showUnobtainableTitles: payload.showUnobtainableTitles === true,
    fishGuideProgress: null,
    payload: clone(payload),
    conflict: value.kind === "conflict" ? clone(value) : null,
    reason: value.reason ?? null,
  });
}

function boundedGuideProjection(value) {
  if (!value || typeof value !== "object") {
    return frozenClone({
      kind: "uninitialized",
      revision: null,
      evidenceRevision: null,
      activeRevision: null,
      activeEvidenceId: null,
      fishGuideProgress: null,
      reason: null,
      authoritative: null,
    });
  }
  return frozenClone({
    kind: value.kind ?? "uninitialized",
    revision: value.revision ?? null,
    evidenceRevision: value.evidenceRevision ?? null,
    activeRevision: value.activeRevision ?? null,
    activeEvidenceId: value.activeEvidenceId ?? null,
    fishGuideProgress: value.fishGuideProgress ?? null,
    reason: value.reason ?? null,
    authoritative: value.authoritative
      ? boundedGuideProjection(value.authoritative)
      : null,
  });
}

function combineCurrentProjection(workspaceProjection, guideProjection) {
  const workspace = workspaceProjection ?? normalizeWorkspaceProjection(null);
  const guide = boundedGuideProjection(guideProjection);
  const workspaceOwnsPayload = workspace.payload !== null;
  const guideReady = guide.kind === "ready" || guide.kind === "missing";
  const kind = workspaceOwnsPayload && !guideReady
    ? guide.kind
    : workspace.kind;
  return frozenClone({
    kind,
    revision: `${workspace.revision ?? "none"}|guide:${guide.revision ?? "none"}`,
    workspaceRevision: workspace.workspaceRevision,
    guideRevision: guide.revision ?? null,
    guideActiveRevision: guide.activeRevision ?? null,
    caughtIds: workspace.caughtIds,
    favouriteIds: workspace.favouriteIds,
    showDried: workspace.showDried,
    catchCounts: workspace.catchCounts,
    obtainedTitleIds: workspace.obtainedTitleIds,
    showContestTitles: workspace.showContestTitles,
    showUnobtainableTitles: workspace.showUnobtainableTitles,
    fishGuideProgress: guideReady
      ? guide.fishGuideProgress ?? { status: "unknown", snapshot: null }
      : guide.authoritative?.fishGuideProgress ?? null,
    payload: workspace.payload,
    conflict: workspace.conflict
      ?? (guide.kind === "conflict" ? clone(guide) : null),
    reason: workspace.reason ?? guide.reason ?? null,
    workspace: clone(workspace),
    guide: clone(guide),
  });
}

function rawLegacy(provider) {
  const records = {};
  for (const [name, key] of Object.entries(LEGACY_KEYS)) {
    records[name] = provider.getItem(key);
  }
  return records;
}

function decodeLegacy(records, fishStateContract) {
  if (Object.values(records).every((raw) => raw === null)) {
    return { kind: "missing", payload: defaultPayload(), records };
  }
  if (Object.values(records).some((raw) => raw === "")) {
    throw new TypeError("Legacy Fishydex state contains an empty record");
  }
  const caughtIds = records.caught === null
    ? []
    : canonicalIds(JSON.parse(records.caught), "Legacy caught IDs");
  const favouriteIds = records.favourites === null
    ? []
    : canonicalIds(JSON.parse(records.favourites), "Legacy favourite IDs");
  const guideSnapshot = records.guide === null
    ? undefined
    : fishStateContract.normalizeFishGuideSnapshot(JSON.parse(records.guide));
  let showDried = false;
  if (records.ui !== null) {
    const ui = JSON.parse(records.ui);
    if (!ui || typeof ui !== "object" || Array.isArray(ui)) {
      throw new TypeError("Legacy Fishydex UI state must be an object");
    }
    if (Object.prototype.hasOwnProperty.call(ui, "show_dried")) {
      showDried = boolean(ui.show_dried, "Legacy Fishydex dried-item preference");
    }
  }
  return {
    kind: "ready",
    records,
    payload: decodeFishydexPresetPayload({
      caughtIds,
      favouriteIds,
      showDried,
    }),
    guideSnapshot,
  };
}

function payloadsEqual(left, right) {
  return JSON.stringify(decodeFishydexPresetPayload(left))
    === JSON.stringify(decodeFishydexPresetPayload(right));
}

export function createFishydexCurrentRepository({
  workspace,
  storage,
  evidenceRepository,
  fishStateContract = globalThis.window?.__fishystuffSharedFishState,
} = {}) {
  const owner = requireWorkspace(workspace);
  const legacyProvider = requireStorage(fishydexBrowserStorage(storage));
  const evidence = requireEvidence(evidenceRepository);
  if (typeof fishStateContract?.normalizeFishGuideSnapshot !== "function"
      || typeof fishStateContract?.createFishGuideCountLookup !== "function") {
    throw new TypeError("Fish Guide snapshot contract is unavailable");
  }
  const listeners = new Set();
  let workspaceProjection = normalizeWorkspaceProjection(owner.state(COLLECTION_KEY));
  let guideProjection = evidence.state();
  let state = combineCurrentProjection(workspaceProjection, guideProjection);
  let publishedGuideProjection = guideProjection;
  let loadPromise = null;
  let loaded = false;
  let disposed = false;

  function alive() {
    if (disposed) {
      throw new Error("Fishydex Current repository is disposed");
    }
  }

  function publishWorkspace(nextWorkspace) {
    const next = normalizeWorkspaceProjection(nextWorkspace);
    // Workspace commands return the same authority already delivered by their
    // subscription. A ready revision owns its payload; acknowledgements still
    // return through commandResult without republishing that unchanged state.
    if (state.kind === "ready" && next.kind === "ready"
        && workspaceProjection.kind === "ready"
        && typeof next.workspaceRevision === "string" && next.workspaceRevision
        && next.workspaceRevision === workspaceProjection.workspaceRevision
        && next.reason === workspaceProjection.reason
        && publishedGuideProjection === guideProjection) {
      return state;
    }
    workspaceProjection = next;
    state = combineCurrentProjection(workspaceProjection, guideProjection);
    publishedGuideProjection = guideProjection;
    return notify();
  }

  function publishGuide(nextGuide) {
    guideProjection = nextGuide;
    state = combineCurrentProjection(workspaceProjection, guideProjection);
    publishedGuideProjection = guideProjection;
    return notify();
  }

  function notify() {
    for (const listener of listeners) {
      listener(state);
    }
    return state;
  }

  const unsubscribeWorkspace = owner.subscribe(COLLECTION_KEY, (next) => {
    if (!disposed) {
      publishWorkspace(next);
    }
  });
  const unsubscribeGuide = evidence.subscribe((next) => {
    if (!disposed) {
      publishGuide(next);
    }
  });

  function legacyExport(kind = "ready", reason = null) {
    let records;
    let transition;
    try {
      records = rawLegacy(legacyProvider);
      transition = fishydexLegacySemanticTransition(legacyProvider);
    } catch (error) {
      return frozenClone({
        kind: "unavailable",
        records: [],
        transition: null,
        reason: String(error?.message || error),
      });
    }
    return frozenClone({
      kind,
      reason,
      records: Object.entries(LEGACY_KEYS).map(([name, key]) => ({
        name,
        key,
        raw: records[name],
      })),
      transition: {
        key: FISHYDEX_LEGACY_SEMANTIC_TRANSITION_KEY,
        ...transition,
      },
    });
  }

  function removeLegacySemantic() {
    for (const key of [LEGACY_KEYS.caught, LEGACY_KEYS.favourites, LEGACY_KEYS.guide]) {
      removeAndVerify(legacyProvider, key);
    }
  }

  function legacyFailure(workspaceState, kind, reason) {
    return frozenClone({
      ...combineCurrentProjection(normalizeWorkspaceProjection(workspaceState), guideProjection),
      kind,
      reason,
      legacy: legacyExport(kind, reason),
    });
  }

  function readTransition(workspaceState) {
    try {
      return fishydexLegacySemanticTransition(legacyProvider);
    } catch (error) {
      return legacyFailure(
        workspaceState,
        "unavailable",
        String(error?.message || error),
      );
    }
  }

  function writeTransition(workspaceState, receipt) {
    try {
      return writeFishydexLegacySemanticTransition(legacyProvider, receipt);
    } catch (error) {
      return legacyFailure(
        workspaceState,
        "unavailable",
        String(error?.message || error),
      );
    }
  }

  function finishAcknowledgedTransition(workspaceState) {
    try {
      removeLegacySemantic();
      removeFishydexLegacySemanticTransition(legacyProvider);
      return workspaceState;
    } catch (error) {
      return legacyFailure(
        workspaceState,
        "unavailable",
        String(error?.message || error),
      );
    }
  }

  async function resetWorkspaceToDefault(workspaceState) {
    let current = workspaceState;
    if (current.kind === "ready") {
      current = await owner.clearCurrent(COLLECTION_KEY, {
        expectedRevision: current.revision,
      });
    } else if (current.kind === "invalid" || current.kind === "unsupportedVersion") {
      current = await owner.clearCurrent(COLLECTION_KEY, { resetInvalid: true });
    }
    if (current.kind !== "missing") {
      return current;
    }
    return owner.initialize(COLLECTION_KEY, defaultPayload(), {
      source: { kind: "fixed", id: "default" },
      expectedRevision: current.revision,
    });
  }

  async function resumeTransition(workspaceState, transition) {
    if (transition.kind === "invalid" || transition.kind === "unsupportedVersion") {
      return legacyFailure(workspaceState, transition.kind, transition.reason);
    }
    if (transition.kind === "missing") {
      return null;
    }
    let current = workspaceState;
    let receipt = transition.receipt;
    if (receipt.transition === "discard" && receipt.phase === "requested") {
      current = await resetWorkspaceToDefault(current);
      const projection = normalizeWorkspaceProjection(current);
      if (
        projection.kind !== "ready"
        || projection.payload === null
        || !payloadsEqual(projection.payload, defaultPayload())
      ) {
        return legacyFailure(
          current,
          current.kind === "conflict" ? "conflict" : current.kind,
          current.reason || "legacy-discard-current-not-acknowledged",
        );
      }
      receipt = { ...receipt, phase: "acknowledged" };
      const written = writeTransition(current, receipt);
      if (written.kind !== "ready") {
        return written;
      }
    }
    return finishAcknowledgedTransition(current);
  }

  async function migrateLegacy(workspaceState) {
    const transition = readTransition(workspaceState);
    if (transition.kind === "invalid"
        || transition.kind === "unsupportedVersion"
        || transition.kind === "unavailable") {
      return transition;
    }
    const resumed = await resumeTransition(workspaceState, transition);
    if (resumed !== null) {
      return resumed;
    }
    let legacy;
    try {
      legacy = decodeLegacy(rawLegacy(legacyProvider), fishStateContract);
    } catch (error) {
      return legacyFailure(workspaceState, "invalid", String(error?.message || error));
    }
    if (legacy.kind === "missing") {
      return workspaceState;
    }
    let importedEvidenceId = null;
    if (legacy.guideSnapshot) {
      const evidenceState = await evidence.ensure(legacy.guideSnapshot);
      guideProjection = evidence.state();
      if (evidenceState.kind !== "ready") {
        return frozenClone({
          ...combineCurrentProjection(
            normalizeWorkspaceProjection(workspaceState),
            guideProjection,
          ),
          kind: evidenceState.kind,
          reason: evidenceState.reason,
          legacy: legacyExport(),
        });
      }
      importedEvidenceId = evidenceState.evidenceId;
      const resolved = evidence.resolve(importedEvidenceId);
      if (
        resolved.kind !== "ready"
        || JSON.stringify(resolved.snapshot) !== JSON.stringify(legacy.guideSnapshot)
      ) {
        return frozenClone({
          ...combineCurrentProjection(
            normalizeWorkspaceProjection(workspaceState),
            evidence.state(),
          ),
          kind: resolved.kind === "missing" ? "conflict" : resolved.kind,
          reason: resolved.reason || "legacy-guide-evidence-not-acknowledged",
          legacy: legacyExport(),
        });
      }
      guideProjection = evidence.state();
      if (
        guideProjection.activeRevision > 0
        && guideProjection.activeEvidenceId !== importedEvidenceId
      ) {
        return frozenClone({
          ...combineCurrentProjection(
            normalizeWorkspaceProjection(workspaceState),
            guideProjection,
          ),
          kind: "conflict",
          reason: "legacy-guide-active-reference-diverged",
          legacy: legacyExport("conflict", "legacy-guide-active-reference-diverged"),
        });
      }
    }
    let acknowledged = workspaceState;
    if (workspaceState.kind === "missing") {
      acknowledged = await owner.initialize(COLLECTION_KEY, legacy.payload, {
        source: { kind: "fixed", id: "default" },
        expectedRevision: workspaceState.revision,
        initialEdit: {
          actionId: operationId(undefined, "fishydex-legacy-import"),
          actionKind: "legacy-import",
        },
      });
    }
    const projection = normalizeWorkspaceProjection(acknowledged);
    if (
      projection.payload === null
      || !payloadsEqual(projection.payload, legacy.payload)
    ) {
      return frozenClone({
        ...projection,
        kind: "conflict",
        reason: "legacy-current-diverged",
        legacy: legacyExport("conflict", "legacy-current-diverged"),
      });
    }
    if (importedEvidenceId !== null) {
      const active = await evidence.activate(importedEvidenceId, {
        expectedRevision: guideProjection.activeRevision,
      });
      guideProjection = evidence.state();
      if (
        active.kind !== "ready"
        || active.activeEvidenceId !== importedEvidenceId
        || active.fishGuideProgress?.status !== "available"
      ) {
        return frozenClone({
          ...combineCurrentProjection(projection, guideProjection),
          kind: active.kind === "missing" ? "conflict" : active.kind,
          reason: active.reason || "legacy-guide-activation-not-acknowledged",
          legacy: legacyExport(),
        });
      }
    }
    const receipt = writeTransition(acknowledged, {
      schemaVersion: 1,
      transition: "import",
      phase: "acknowledged",
      operationId: operationId(undefined, "fishydex-legacy-import-receipt"),
    });
    if (receipt.kind !== "ready") {
      return receipt;
    }
    return finishAcknowledgedTransition(acknowledged);
  }

  async function load() {
    alive();
    if (loaded) {
      return state;
    }
    if (!loadPromise) {
      loadPromise = (async () => {
        guideProjection = evidence.load();
        let current = await owner.load(COLLECTION_KEY);
        current = await migrateLegacy(current);
        if (current.kind === "missing") {
          current = await owner.initialize(COLLECTION_KEY, defaultPayload(), {
            source: { kind: "fixed", id: "default" },
            expectedRevision: current.revision,
          });
        }
        if (["invalid", "unsupportedVersion", "unavailable", "conflict"].includes(current.kind)
            && !current.current) {
          workspaceProjection = current.workspace
            ?? (Object.prototype.hasOwnProperty.call(current, "caughtIds")
              ? current
              : normalizeWorkspaceProjection(current));
          state = frozenClone({
            ...combineCurrentProjection(workspaceProjection, guideProjection),
            kind: current.kind,
            reason: current.reason ?? null,
            ...(current.legacy ? { legacy: current.legacy } : {}),
          });
          loaded = current.kind !== "unavailable";
          return notify();
        }
        loaded = true;
        return publishWorkspace(current);
      })().finally(() => {
        loadPromise = null;
      });
    }
    return loadPromise;
  }

  async function readyState() {
    let current = await load();
    if (
      current.kind !== "ready"
      && (current.guide?.kind === "conflict" || current.guide?.kind === "unavailable")
    ) {
      guideProjection = evidence.load();
      current = combineCurrentProjection(workspaceProjection, guideProjection);
      state = current;
    }
    return current;
  }

  async function editTransform(transform, command) {
    alive();
    let current = await readyState();
    if (current.kind !== "ready") {
      return commandResult(current, command.operationId);
    }
    if (
      command.expectedRevision !== undefined
      && command.expectedRevision !== current.workspaceRevision
    ) {
      return commandResult(current, command.operationId, {
        kind: "conflict",
        reason: "expected-workspace-revision",
        expectedRevision: command.expectedRevision,
        actualRevision: current.workspaceRevision,
        authoritative: current,
      });
    }
    const retryConflict = command.expectedRevision === undefined;
    for (let attempt = 0; attempt < (retryConflict ? 2 : 1); attempt += 1) {
      const payload = decodeFishydexPresetPayload(transform(clone(current.payload), current));
      const result = await owner.edit(COLLECTION_KEY, payload, {
        expectedRevision: current.workspaceRevision,
        actionId: command.operationId,
        actionKind: command.actionKind,
      });
      if (result.kind !== "conflict") {
        return commandResult(publishWorkspace(result), command.operationId);
      }
      const authoritative = combineCurrentProjection(
        normalizeWorkspaceProjection(result.authoritative),
        guideProjection,
      );
      if (!retryConflict || authoritative.kind !== "ready") {
        const acknowledged = authoritative.kind === "ready"
          ? publishWorkspace(result.authoritative)
          : state;
        return commandResult(acknowledged, command.operationId, {
          kind: "conflict",
          reason: result.reason ?? "workspace-revision-changed",
          expectedRevision: current.workspaceRevision,
          actualRevision: authoritative.workspaceRevision,
          authoritative: acknowledged,
        });
      }
      current = authoritative;
    }
    return commandResult(state, command.operationId, {
      kind: "conflict",
      reason: "workspace-revision-changed",
      authoritative: state,
    });
  }

  function setMembership(payload, field, id, enabled) {
    const selected = new Set(payload[field]);
    if (enabled) {
      selected.add(id);
    } else {
      selected.delete(id);
    }
    payload[field] = Array.from(selected).sort((left, right) => left - right);
    return payload;
  }

  async function setCaught(idValue, value, options = {}) {
    const id = positiveItemId(idValue);
    const enabled = boolean(value, "Fishydex caught intent");
    const command = titleEditOptions(options, "caught-intent");
    const titles = catchTitleIntents(id, command.titles);
    return editTransform(
      (payload, current) => {
        const count = projectTitleProgress({ itemId: id }, [], payload, guideCountForCommand(current, command)).count;
        pruneCatchTitleOwnership(payload, titles);
        return writeCatchCount(payload, id, enabled ? Math.max(count.kind === "known" ? count.count : 0, 1) : 0);
      },
      command,
    );
  }

  async function setFavourite(idValue, value, options = {}) {
    const id = positiveItemId(idValue);
    const enabled = boolean(value, "Fishydex favourite intent");
    return editTransform(
      (payload) => setMembership(payload, "favouriteIds", id, enabled),
      currentEditOptions(options, "favourite-intent"),
    );
  }

  async function setShowDried(value, options = {}) {
    const enabled = boolean(value, "Fishydex dried-item intent");
    return editTransform(
      (payload) => ({ ...payload, showDried: enabled }),
      currentEditOptions(options, "dried-display-intent"),
    );
  }

  async function setCatchCount(idValue, value, options = {}) {
    const id = positiveItemId(idValue);
    const count = requireCatchCount(value);
    const command = titleEditOptions(options, "catch-count-intent");
    const titles = catchTitleIntents(id, command.titles);
    return editTransform(
      (payload) => writeCatchCount(pruneCatchTitleOwnership(payload, titles), id, count),
      command,
    );
  }

  function guideCountForCommand(current, command) {
    return command.encyclopediaKey === undefined
      ? { kind: "unknown", count: null }
      : fishStateContract.createFishGuideCountLookup(current.fishGuideProgress)(command.encyclopediaKey);
  }

  async function setTitleOwned(title, value, options = {}) {
    const intent = titleProgressIntent(title);
    const owned = boolean(value, "Fishydex title ownership");
    const command = titleEditOptions(options, "title-ownership-intent");
    const titles = catchTitleIntents(intent.itemId, command.titles);
    return editTransform(
      (payload, current) => applyTitleOwnership(pruneCatchTitleOwnership(payload, titles),
        intent, owned, guideCountForCommand(current, command)),
      command,
    );
  }

  async function setShowContestTitles(value, options = {}) {
    const enabled = boolean(value, "Fishydex contest-title intent");
    return editTransform(
      (payload) => ({ ...payload, showContestTitles: enabled }),
      currentEditOptions(options, "contest-titles-display-intent"),
    );
  }

  async function setShowUnobtainableTitles(value, options = {}) {
    const enabled = boolean(value, "Fishydex unobtainable-title intent");
    return editTransform(
      (payload) => ({ ...payload, showUnobtainableTitles: enabled }),
      currentEditOptions(options, "unobtainable-titles-display-intent"),
    );
  }

  async function activateFishGuideSnapshot(snapshotValue, options = {}) {
    alive();
    const command = guideActivationOptions(options);
    const current = await readyState();
    if (current.kind !== "ready") {
      return commandResult(current, command.operationId);
    }
    if (
      command.expectedRevision !== undefined
      && command.expectedRevision !== current.revision
    ) {
      return commandResult(current, command.operationId, {
        kind: "conflict",
        reason: "expected-revision",
        expectedRevision: command.expectedRevision,
        actualRevision: current.revision,
        authoritative: current,
      });
    }
    const snapshot = snapshotValue === null
      ? null
      : fishStateContract.normalizeFishGuideSnapshot(snapshotValue);
    let id = null;
    if (snapshot) {
      const evidenceState = await evidence.ensure(snapshot, {
        expectedRevision: current.guide.evidenceRevision,
        operationId: command.operationId,
      });
      guideProjection = evidence.state();
      if (evidenceState.kind !== "ready") {
        return commandResult(publishGuide(guideProjection), command.operationId);
      }
      id = evidenceState.evidenceId;
      const resolved = evidence.resolve(id);
      if (
        resolved.kind !== "ready"
        || JSON.stringify(resolved.snapshot) !== JSON.stringify(snapshot)
      ) {
        guideProjection = evidence.state();
        state = frozenClone({
          ...combineCurrentProjection(workspaceProjection, guideProjection),
          kind: resolved.kind === "missing" ? "conflict" : resolved.kind,
          reason: resolved.reason || "Fish Guide evidence was not acknowledged",
        });
        return commandResult(notify(), command.operationId);
      }
    }
    const active = await evidence.activate(id, {
      expectedRevision: command.expectedGuideRevision ?? current.guideActiveRevision,
      operationId: command.operationId,
    });
    guideProjection = evidence.state();
    if (active.kind !== "ready") {
      return commandResult(publishGuide(active), command.operationId);
    }
    return commandResult(publishGuide(guideProjection), command.operationId);
  }

  async function resetCurrentData() {
    alive();
    if (guideProjection.kind === "uninitialized") {
      guideProjection = evidence.load();
    }
    let workspaceState = owner.state(COLLECTION_KEY);
    let transition = readTransition(workspaceState);
    if (transition.kind === "invalid"
        || transition.kind === "unsupportedVersion"
        || transition.kind === "unavailable") {
      state = transition;
      return notify();
    }
    if (transition.kind === "ready" && transition.receipt.transition === "import") {
      const cleaned = finishAcknowledgedTransition(workspaceState);
      if (cleaned.kind === "unavailable") {
        state = cleaned;
        return notify();
      }
      transition = { kind: "missing" };
    }
    if (transition.kind === "missing") {
      const written = writeTransition(workspaceState, {
        schemaVersion: 1,
        transition: "discard",
        phase: "requested",
        operationId: operationId(undefined, "fishydex-legacy-discard"),
      });
      if (written.kind !== "ready") {
        state = written;
        return notify();
      }
      transition = written;
    }
    workspaceState = await owner.load(COLLECTION_KEY);
    const reset = await resumeTransition(workspaceState, transition);
    if (Object.prototype.hasOwnProperty.call(reset, "caughtIds")) {
      state = frozenClone(reset);
      return notify();
    }
    loaded = reset.kind === "ready";
    return publishWorkspace(reset);
  }

  const unbindRuntime = owner.bindCollectionRuntime(COLLECTION_KEY, {
    captureCurrent() {
      alive();
      return state.payload === null ? undefined : clone(state.payload);
    },
    async applyCurrent(payloadValue) {
      alive();
      const payload = decodeFishydexPresetPayload(payloadValue);
      return clone(payload);
    },
  });

  return Object.freeze({
    [FISHYDEX_CURRENT_BRAND]: 1,
    collectionKey: COLLECTION_KEY,
    load,
    state() {
      alive();
      return state;
    },
    setCaught,
    setFavourite,
    setShowDried,
    activateFishGuideSnapshot,
    setCatchCount,
    setTitleOwned,
    setShowContestTitles,
    setShowUnobtainableTitles,
    resetCurrentData,
    exportRawCollection() {
      alive();
      return owner.exportRawCollection(COLLECTION_KEY);
    },
    exportLegacyRaw() {
      alive();
      return legacyExport();
    },
    subscribe(listener) {
      alive();
      if (typeof listener !== "function") {
        throw new TypeError("Fishydex Current subscriber must be a function");
      }
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      listeners.clear();
      unbindRuntime?.();
      unsubscribeWorkspace?.();
      unsubscribeGuide?.();
    },
  });
}

export function browserFishydexCurrentRepository(options = {}) {
  const windowRef = options.window ?? globalThis.window;
  const occupied = windowRef?.[SLOT];
  if (occupied !== undefined) {
    if (occupied?.[FISHYDEX_CURRENT_BRAND] !== 1) {
      throw new TypeError("Shared Fishydex Current repository is incompatible");
    }
    return occupied;
  }
  const storage = fishydexBrowserStorage(options.storage);
  const evidenceRepository = options.evidenceRepository
    ?? browserFishydexGuideEvidenceRepository({
      window: windowRef,
      storage,
      eventTarget: options.eventTarget ?? windowRef,
      fishStateContract: options.fishStateContract
        ?? windowRef?.__fishystuffSharedFishState,
    });
  const repository = createFishydexCurrentRepository({
    workspace: options.workspace ?? browserPresetWorkspace({
      window: windowRef,
      storage,
    }),
    storage,
    evidenceRepository,
    fishStateContract: options.fishStateContract
      ?? windowRef?.__fishystuffSharedFishState,
  });
  if (windowRef && typeof windowRef === "object") {
    windowRef[SLOT] = repository;
  }
  return repository;
}
